import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    CreatePracticeWeek,
    DeletePracticeWeeks,
    EditPracticeWeeks,
    GetPracticeWeeks,
} from "./thunk";
import { IPracticeWeekInitialState, IPracticeWeeksPayload } from "../types";

const initialState: IPracticeWeekInitialState = {
    practiceWeeks: [],
    practiceWeeksRestore: [],
    isLoadingPracticeWeeks: false,
    isViewPracticeWeeksRestore: false,
    selectedPracticeWeeksIDs: [],
};

const practiceWeekSlice = createSlice({
    name: "practiceWeeks",
    initialState,
    reducers: {
        setPracticeWeeks: (
            state,
            action: PayloadAction<IPracticeWeeksPayload[]>
        ) => {
            state.practiceWeeks = action.payload.filter(
                (elem: IPracticeWeeksPayload) => elem.kind === "practice"
            );
        },

        setIsViewPracticeWeeksRestore: (state) => {
            state.isViewPracticeWeeksRestore =
                !state.isViewPracticeWeeksRestore;
        },

        deletePracticeWeek: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.practiceWeeks = state.practiceWeeks.filter(
                (elem: IPracticeWeeksPayload) => elem.id !== id
            );
        },

        updatePracticeWeek: (
            state,
            action: PayloadAction<{
                updatedPracticeWeek: IPracticeWeeksPayload;
            }>
        ) => {
            const { updatedPracticeWeek } = action.payload;
            state.practiceWeeks = state.practiceWeeks.map(
                (elem: IPracticeWeeksPayload) => {
                    if (elem.id === updatedPracticeWeek.id) {
                        return {
                            ...elem,
                            ...updatedPracticeWeek,
                        };
                    }
                    return elem;
                }
            );
        },

        setSelectedPracticeWeeksIDs: (
            state,
            action: PayloadAction<number[]>
        ) => {
            state.selectedPracticeWeeksIDs = action.payload;
        },

        addPracticeWeek: (
            state,
            action: PayloadAction<IPracticeWeeksPayload>
        ) => {
            state.practiceWeeks.push(action.payload);
        },

        togglePracticeWeekActive: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            const practiceWeek = state.practiceWeeks.find(
                (elem) => elem.id === id
            );
            if (practiceWeek) {
                practiceWeek.is_active = !practiceWeek.is_active;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Practice Weeks
            .addCase(GetPracticeWeeks.pending, (state) => {
                state.isLoadingPracticeWeeks = true;
            })
            .addCase(GetPracticeWeeks.fulfilled, (state, action) => {
                state.isLoadingPracticeWeeks = false;
                state.practiceWeeks = action.payload.filter(
                    (elem: IPracticeWeeksPayload) => elem.kind === "practice"
                );
            })
            .addCase(GetPracticeWeeks.rejected, (state) => {
                state.isLoadingPracticeWeeks = false;
            })
            // Create Academic Week
            .addCase(CreatePracticeWeek.pending, (state) => {
                state.isLoadingPracticeWeeks = true;
            })
            .addCase(CreatePracticeWeek.fulfilled, (state, action) => {
                state.isLoadingPracticeWeeks = false;
                state.practiceWeeks.push(
                    ...action.payload.filter(
                        (elem: IPracticeWeeksPayload) =>
                            elem.kind === "practice"
                    )
                );
            })
            .addCase(CreatePracticeWeek.rejected, (state) => {
                state.isLoadingPracticeWeeks = false;
            })
            // Update Academic Week
            .addCase(EditPracticeWeeks.pending, (state) => {
                state.isLoadingPracticeWeeks = true;
            })
            .addCase(EditPracticeWeeks.fulfilled, (state, action) => {
                state.isLoadingPracticeWeeks = false;
                const index = state.practiceWeeks.findIndex(
                    (week) => week.id === action.payload.id
                );
                if (index !== -1) {
                    state.practiceWeeks[index] = action.payload;
                }
            })
            .addCase(EditPracticeWeeks.rejected, (state) => {
                state.isLoadingPracticeWeeks = false;
            })
            // Delete Academic Week
            .addCase(DeletePracticeWeeks.pending, (state) => {
                state.isLoadingPracticeWeeks = true;
            })
            .addCase(DeletePracticeWeeks.fulfilled, (state, action) => {
                state.isLoadingPracticeWeeks = false;
                state.practiceWeeks = state.practiceWeeks.filter(
                    (week) => week.id !== action.payload
                );
            })
            .addCase(DeletePracticeWeeks.rejected, (state) => {
                state.isLoadingPracticeWeeks = false;
            });
    },
});

export const {
    setPracticeWeeks,
    setSelectedPracticeWeeksIDs,
    deletePracticeWeek,
    updatePracticeWeek,
    setIsViewPracticeWeeksRestore,
    addPracticeWeek,
    togglePracticeWeekActive,
} = practiceWeekSlice.actions;

export default practiceWeekSlice;
